import {
  FC,
  MouseEvent,
  ReactElement,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Range } from "react-date-range";
import { Box, Button, Stack, Typography } from "@mui/material";
import { Event, ExpandMore } from "@mui/icons-material";

import CustomDateRangePicker from "../date-range-picker";
import { formatDateOrTime } from "@/utils";
import { PRESET_RANGES } from "@/constants";
import { responsiveFontSizes } from "@/utils/responsive";

interface IDateRangeFilter {
  value?: Pick<Range, "startDate" | "endDate">;
  onSelectDate?: (dates: Pick<Range, "startDate" | "endDate">) => void;
  fullWidth?: boolean;
}

const DateRangeFilter: FC<IDateRangeFilter> = ({
  value,
  onSelectDate,
  fullWidth = false,
}): ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const openDatePickerModal = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const dateLabel = useMemo(() => {
    const hasStart = Boolean(value?.startDate);
    const hasEnd = Boolean(value?.endDate);

    if (!hasStart && !hasEnd) {
      return "All Time";
    }

    if (hasStart && hasEnd) {
      const selectedStartDate = formatDateOrTime(value!.startDate!, "date");
      const selectedEndDate = formatDateOrTime(value!.endDate!, "date");

      const presetSingle = PRESET_RANGES.find((preset) => {
        const presetStartDate = formatDateOrTime(
          preset.range.startDate,
          "date"
        );
        const presetEndDate = formatDateOrTime(preset.range.endDate, "date");

        return (
          selectedStartDate === presetStartDate &&
          selectedEndDate === presetEndDate
        );
      });

      return presetSingle?.label ?? `${selectedStartDate} - ${selectedEndDate}`;
    }

    if (hasStart && !hasEnd) {
      return `${formatDateOrTime(value!.startDate!, "date")} - Present`;
    }

    if (!hasStart && hasEnd) {
      return `Until ${formatDateOrTime(value!.endDate!, "date")}`;
    }

    return "All Time";
  }, [value]);

  return (
    <>
      <Button
        variant="white-contained"
        onClick={openDatePickerModal}
        id="date-range-picker-anchor"
        aria-controls={anchorEl ? "date-range-picker-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={anchorEl ? "true" : undefined}
        sx={{
          "&[aria-expanded='true']": {
            md: {
              zIndex: 1500,
            },
          },
          border: "1px solid #E6E7E7",
          boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
          minWidth: fullWidth ? "100%" : 210,
          py: 0.5,
          px: 1,
          flex: {
            xs: 1,
            sm: "initial",
          },
          ...(fullWidth && {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }),
        }}
      >
        <Stack direction="row" alignItems={"center"} justifyContent={"space-between"} width={"100%"}>
          <Box display={"flex"} alignItems={"center"} gap={0.5}>
          <Event sx={{ fontSize: responsiveFontSizes.base, color: "neutral.500" }} />
          <Typography
            variant="body2"
            color="neutral.500"
            sx={{ fontSize: responsiveFontSizes.xs }}
            fontWeight={500}
          >
            Show :
          </Typography>
          <Typography color="common.black" sx={{ fontSize: responsiveFontSizes.xs }} fontWeight={500}>
            {dateLabel}
          </Typography>
          </Box>

          <ExpandMore
            sx={{
              color: "neutral.500",
              fontSize: fullWidth ? 18 : 16,
            }}
          />
        </Stack>
      </Button>
      <CustomDateRangePicker
        anchorEl={anchorEl}
        rangeValue={value}
        onClose={() => setAnchorEl(null)}
        onDateSelect={onSelectDate}
      />
    </>
  );
};

export default DateRangeFilter;
